import React, { useEffect } from 'react';

import Editor from '@erikdakoda/editor-ui/Editor';
import PublishControl from '@erikdakoda/editor-ui/PublishControl';
import { useFindUniqueToolSet, useUpdateToolSet } from '@erikdakoda/database/hooks/tool-set';
import type { ToolSetUpdateArgs } from '@erikdakoda/database/types';
import {
  useCreateToolSetEntry,
  useDeleteToolSetEntry,
  useUpdateToolSetEntry,
} from '@erikdakoda/database/hooks/tool-set-entry';
import { modelSchema, modelUpdateSchema } from '@erikdakoda/database/zodSchemas';

import type { CustomControl, FieldSpecs } from '@erikdakoda/editor-ui/types';
import type { FieldValues } from 'react-hook-form';
import { ToolSetForEditSelect } from '@erikdakoda/tool-set/types';

import { useToolSetGridStore } from '../hooks/useToolSetGridStore';
import ToolSetEditorControl from './ToolSetEditorControl';
import {
  entriesFromToolSet,
  resolveEntryDraftsForSave,
  updateToolSetAndEntries,
} from './updateToolSetAndEntries';
import type { GridEditorDialogLoadingProps } from '@erikdakoda/data-grid-ui/types/gridEditorDialog';

const ToolSetSchema = modelSchema('ToolSet');
const ToolSetUpdateSchema = modelUpdateSchema('ToolSet');

export type ToolSetEditorProps = GridEditorDialogLoadingProps & {
  toolSetId: string;
  dialog?: boolean;
  refetch: (() => Promise<void>) | null;
  onClose?: () => void;
};

export default function ToolSetEditor({
  toolSetId,
  dialog = false,
  refetch,
  onClose,
  onLoadingChange,
}: ToolSetEditorProps) {
  'use memo';

  const setEditedToolSetId = useToolSetGridStore((state) => state.setEditedToolSetId);
  const updateToolSet = useUpdateToolSet().mutateAsync;
  const createToolSetEntry = useCreateToolSetEntry().mutateAsync;
  const updateToolSetEntry = useUpdateToolSetEntry().mutateAsync;
  const deleteToolSetEntry = useDeleteToolSetEntry().mutateAsync;

  const {
    data: toolSet,
    isLoading,
    refetch: refetchToolSet,
  } = useFindUniqueToolSet({
    where: { id: toolSetId },
    select: ToolSetForEditSelect,
  });

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  async function updateToolSetWithEntries(updateArgs: ToolSetUpdateArgs): Promise<void> {
    if (!toolSet) return;
    const entryChanges = (updateArgs.data as { entries?: unknown }).entries;
    const data = { ...updateArgs.data };
    delete (data as { entries?: unknown }).entries;
    const baselineEntries = entriesFromToolSet(toolSet.entries);

    await updateToolSetAndEntries({
      toolSet: {
        id: toolSet.id,
        ownerId: toolSet.ownerId,
        spaceId: toolSet.spaceId,
      },
      updateArgs: { ...updateArgs, data },
      entries: resolveEntryDraftsForSave(baselineEntries, entryChanges),
      updateToolSet,
      createToolSetEntry,
      updateToolSetEntry,
      deleteToolSetEntry,
      existingEntryIds: (toolSet.entries ?? []).map((e) => e.id),
      baselineEntries,
    });
  }

  function handleClose(): void {
    setEditedToolSetId(null);
    onClose?.();
  }

  async function handleSuccess(
    _updatedItem: unknown,
    _changes: FieldValues,
    continueEditing: boolean,
  ): Promise<void> {
    await refetchToolSet();
    await refetch?.();
    if (continueEditing) return;
    setEditedToolSetId(null);
    onClose?.();
  }

  const spec: FieldSpecs = {
    description: { rows: 2 },
    publishedAt: {
      component: PublishControl,
    },
    entries: {
      label: 'Tool sources',
      component: ToolSetEditorControl as CustomControl,
      virtual: true,
      columns: 12,
    },
  };

  if (isLoading || !toolSet) return null;

  return (
    <Editor
      className="tool-set-editor"
      item={{
        ...toolSet,
        entries: entriesFromToolSet(toolSet.entries),
      }}
      typeName="ToolSet"
      schema={ToolSetUpdateSchema}
      displaySchema={ToolSetSchema}
      spec={spec}
      fields={['name', 'description', 'publishedAt', 'entries']}
      onCancel={handleClose}
      saveAndContinue={true}
      // @ts-expect-error custom update with entries
      updateItem={updateToolSetWithEntries}
      onSuccess={handleSuccess}
      dialog={dialog}
      dialogFullHeight
      innerWidth="md"
    />
  );
}
